import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppKeyService, RequiredAppVersionAction } from '@app/services/appkey';
import { FeedbackService } from '@services/feedback';
import { AppKeyRequiredVersionEditDialogData } from './appkey-required-version-edit-dialog.models';

@Component({
	selector: 'app-appkey-required-version-edit-dialog',
	templateUrl: './appkey-required-version-edit.dialog.html',
	styleUrls: ['./appkey-required-version-edit.dialog.scss']
})
export class AppKeyRequiredVersionEditDialogComponent implements OnInit {
	public formInputs: FormGroup;
	public actions = Object.values(RequiredAppVersionAction);
	public hasChanges = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public dialogData: AppKeyRequiredVersionEditDialogData,
		private formBuilder: FormBuilder,
		private appKeyService: AppKeyService,
		private feedbackService: FeedbackService,
		private dialogRef: MatDialogRef<AppKeyRequiredVersionEditDialogComponent>
	) {
		this.formInputs = this.buildForm();
	}

	public ngOnInit(): void {
		this.populateForm();
		this.formInputs.valueChanges.subscribe(() => {
			this.hasChanges = this.formInputs.dirty;
		});
	}

	private buildForm(): FormGroup {
		return this.formBuilder.group({
			action: [{ value: '', disabled: this.dialogData.mode === 'edit' }, Validators.required],
			version: ['', [Validators.required, Validators.minLength(1)]],
			buildNumber: ['', [Validators.required, Validators.min(0)]],
			minOSVersion: ['', Validators.required],
			releasedAt: ['', Validators.required]
		});
	}

	private populateForm(): void {
		if (this.dialogData.mode === 'edit' && this.dialogData.version) {
			const v = this.dialogData.version;
			this.formInputs.patchValue({
				action: v.action,
				version: v.version,
				buildNumber: v.buildNumber,
				minOSVersion: v.minOSVersion,
				releasedAt: new Date(v.releasedAt).toISOString().slice(0, 10)
			});
		}
		// For 'create' mode, form is left empty
	}

	public onFormSubmit(): void {
		if (!this.formInputs.valid) {
			this.feedbackService.notification({
				msg: 'Please fill in all required fields correctly'
			});
			return;
		}

		const formValue = this.formInputs.getRawValue();
		const updatedVersion = {
			action: formValue.action,
			version: formValue.version,
			buildNumber: Number(formValue.buildNumber),
			minOSVersion: formValue.minOSVersion,
			releasedAt: new Date(formValue.releasedAt).getTime()
		};

		this.appKeyService.editRequiredVersion(
			this.dialogData.appKey.clientId,
			this.dialogData.platform,
			updatedVersion
		).subscribe(
			() => {
				const message = this.dialogData.mode === 'create'
					? 'Required Version created successfully'
					: 'Required Version updated successfully';
				this.feedbackService.notification({ msg: message });
				this.dialogRef.close(updatedVersion);
			},
			(error: any) => {
				this.feedbackService.apiError(error);
			}
		);
	}

	public close(): void {
		if (this.hasChanges) {
			this.feedbackService.confirm({
				title: 'Unsaved Changes',
				message: 'You have unsaved changes. Do you want to discard them?',
				callback: (confirmed: boolean) => {
					if (confirmed) {
						this.dialogRef.close();
					}
				}
			});
		} else {
			this.dialogRef.close();
		}
	}
}

